import { Checkbox, Message } from '@mairistem/composants';
import {
	getErrorMessage,
	getLibelleImputation,
	type UserRole,
	useEtiquette,
	useUserRole,
} from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import StatutMontantSimulation from '../../constants/simulation/statutMontantSimulation';
import TypePeriode from '../../constants/simulation/typePeriode';
import type DetailMontantSimulation from '../../interfaces/simulation/detailMontantSimulation';
import type {
	default as MontantSimulation,
	MontantSimulationUpdate,
} from '../../interfaces/simulation/montantSimulation';
import type Simulation from '../../interfaces/simulation/simulation';
import {
	deleteMontant,
	updateMontant,
	upsertDemandeMontant,
	upsertMontant,
} from '../../services/simulation/montantSimulation';
import { shouldAddReportSection } from '../../utils/simulation/simulation';
import { NO_SERVICE_OPTION } from '../simulation/Outils/MontantsSimulationModal/MontantsSimulationModalContent';
import MontantsSimulationModalContentItem, {
	type Service,
} from '../simulation/Outils/MontantsSimulationModal/MontantsSimulationModalContentItem';
import './ArbitrageMasseListe.css';

const hasService = (montant: MontantSimulation) =>
	(montant.identifiantSvutCpta ?? 0) > 0;

// Libelle commun a l'appli (imports, controle de coherence...). La section
// peut manquer (job anterieur a son ajout) : getLibelleImputation ne gerant
// pas ce cas, on retire l'espace en trop plutot que d'afficher "undefined"
const getLibelleImputationMontant = (montant: DetailMontantSimulation) =>
	getLibelleImputation(
		montant.sens,
		montant.section ?? '',
		montant.codeChapitre,
		montant.codeArticle,
		montant.codeOperation,
		montant.codeFonction,
		montant.codeVentilation,
		montant.codeAnalytique,
	).replace(/ {2,}/g, ' ');

// Seul le service du montant est propose : il s'affiche donc en texte
// simple, sans selecteur (le changement de service n'est pas l'objet de
// l'arbitrage de masse)
const getServicesPourMontant = (montant: DetailMontantSimulation): Service[] =>
	hasService(montant)
		? [
				{
					'data-testid': `service${montant.libelleService}`,
					key: montant.identifiantSvutCpta,
					text: montant.libelleService ?? '',
					value: montant.identifiantSvutCpta,
				},
			]
		: [NO_SERVICE_OPTION];

// Pas de TreeView a mettre a jour sur cet ecran
const noop = () => {};

interface ArbitrageMasseListeProps {
	montants: DetailMontantSimulation[];
	onDeleteMontant: (identifiant: number) => void;
	onDuplicateMontant: (
		origine: DetailMontantSimulation,
		duplique: MontantSimulation,
	) => void;
	onToggleSelection: (montant: DetailMontantSimulation) => void;
	onUpdateMontant: (montant: MontantSimulation) => void;
	// null quand la selection n'est pas proposee (ex: CA, transfert en compta)
	isSelectionnable: ((montant: DetailMontantSimulation) => boolean) | null;
	isSelectionne: (montant: DetailMontantSimulation) => boolean;
	simulation: Simulation;
	userRole: UserRole;
}

const ArbitrageMasseListe = ({
	isSelectionnable,
	isSelectionne,
	montants,
	onDeleteMontant,
	onDuplicateMontant,
	onToggleSelection,
	onUpdateMontant,
	simulation,
	userRole,
}: ArbitrageMasseListeProps) => {
	const [, userServiceUtilisateur] = useUserRole();

	const EtiquettesInput = useEtiquette(
		'/api/finances/simulation/etiquettes',
		simulation.identifiantEntite,
	);

	const handleChange = async (
		identifiant: number,
		data: MontantSimulationUpdate,
	) => {
		try {
			const montant = await updateMontant(
				{ ...data, identifiant },
				new URLSearchParams({ withEtiquettes: 'true' }),
			);
			onUpdateMontant(montant);
		} catch (e) {
			toast.error(
				getErrorMessage(e, 'Erreur lors de la mise à jour du montant'),
			);
		}
	};

	// Meme enchainement que la duplication de MontantsSimulationModalContent,
	// si ce n'est que le service de la ligne d'origine est conserve
	const handleDuplicate = async (montant: DetailMontantSimulation) => {
		try {
			const data = {
				identifiantAnalytique: montant.identifiantAnalytique ?? null,
				identifiantArticle: montant.identifiantArticle,
				identifiantChapitre: montant.identifiantChapitre,
				identifiantFonction: montant.identifiantFonction,
				identifiantOperation: montant.identifiantOperation,
				identifiantSimulation: simulation.identifiant,
				identifiantVentilation: montant.identifiantVentilation ?? null,
				onlyCreate: true,
				sens: montant.sens,
			};
			const montantCree =
				simulation.typePeriode === TypePeriode.DEMANDE
					? await upsertDemandeMontant(data)
					: await upsertMontant(data);
			const montantDuplique = await updateMontant(
				{
					commentaire: montant.commentaire,
					demande: montant.demande,
					etiquettes: montant.etiquettes,
					identifiant: montantCree.identifiant,
					...(hasService(montant)
						? { identifiantSvutCpta: montant.identifiantSvutCpta }
						: {}),
					prop: montant.prop,
					rar: montant.rar,
					rep: montant.rep,
					statut: StatutMontantSimulation.A_ARBITRER,
					vote: montant.vote,
				},
				new URLSearchParams({ withEtiquettes: 'true' }),
			);
			onDuplicateMontant(montant, montantDuplique);
		} catch (e) {
			toast.error(
				getErrorMessage(e, 'Erreur lors de la duplication du montant'),
			);
		}
	};

	const handleDelete = async (montant: MontantSimulation) => {
		try {
			await deleteMontant(montant.identifiant);
			onDeleteMontant(montant.identifiant);
		} catch (e) {
			toast.error(
				getErrorMessage(e, 'Erreur lors de la suppression du montant'),
			);
		}
	};

	if (montants.length === 0) {
		return (
			<Message
				content="Aucune demande ne correspond"
				data-testid="arbitrageMasseListeVide"
				size="tiny"
			/>
		);
	}

	return (
		<div className="arbitrageMasseListe">
			{montants.map((montant) => (
				<div className="arbitrageMasseListeLigne" key={montant.identifiant}>
					{isSelectionnable && (
						<Checkbox
							checked={isSelectionnable(montant) && isSelectionne(montant)}
							className="arbitrageMasseListeCase"
							data-testid={`arbitrageMasseSelection${montant.identifiant}`}
							disabled={!isSelectionnable(montant)}
							onChange={() => onToggleSelection(montant)}
						/>
					)}
					<MontantsSimulationModalContentItem
						EtiquettesInput={EtiquettesInput}
						identifiantLoginUtilisateur={
							userServiceUtilisateur?.identifiantLogin
						}
						// Admin/ChargePilotage uniquement sur cet ecran : pas de
						// restriction par service (cf isInputDisabled)
						identifiantsServicesUtilisateur={[]}
						libelleImputation={getLibelleImputationMontant(montant)}
						montantSimulation={montant}
						onChange={(data) => handleChange(montant.identifiant, data)}
						onChangeCommentaire={noop}
						onChangeMontant={noop}
						onDelete={handleDelete}
						onDuplicate={() => handleDuplicate(montant)}
						services={getServicesPourMontant(montant)}
						servicesLoading={false}
						showRar={shouldAddReportSection(
							simulation,
							montant.section === 'F',
						)}
						showServiceCombobox={Boolean(
							simulation.exercice?.saisieBudgetService,
						)}
						transfertCpta={simulation.transfertCpta}
						typePeriode={simulation.typePeriode}
						userRole={userRole}
					/>
				</div>
			))}
		</div>
	);
};

export default ArbitrageMasseListe;
